import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { TranslateString } from '@/types/translation.types';

import Modal from '@ui/modal/modal';
import Button from '@ui/buttons/button/button';
import { CreateSpeaker, ESpeakerInviteStatus, Speaker } from '@/types/speakers.types';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import createDefaultSpeaker from '@/utils/speakers/create-default-speaker';
import useLanguageParams from '@/utils/use-language-params';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectGreenroomSessionMeetings,
  selectProjectName,
  selectWorkingProjectDefaultLanguage,
  selectWorkingProjectLanguages,
} from '@/store/slices/project/selectors';
import SendInviteModal from './send-invite-modal';
import { selectUserChannel } from '@/store/slices/user/selectors';
import { GreenroomUserProfile } from '@/types/greenroom/profile.types';
import { selectAdminContacts } from '@/store/slices/channel-admins/selectors';
import useLoadWorkingSessionMeetingData from './use-get-working-session-meeting-data';
import UploadCastImage from './components/upload-cast-img';
import EditFirstName from './components/edit-first-name';
import EditLastName from './components/edit-last-name';
import EditTitle from './components/edit-title';
import EditLocation from './components/edit-location';
import EditEmail from './components/edit-email';
import EditBio from './components/edit-bio';
import useValidateWorkingSpeaker from './helpers/use-validate-working-speaker';
import handleSendGreenroomInvite from './helpers/handle-send-greenroom-invite';
import handleSaveSpeaker from './helpers/handle-save-speaker';
import useInitializeNameGuess from './helpers/use-initialize-name-guess';
import useParamsOrFirstSessionUuid from '@/utils/use-params-or-first-session-uuid';
import useIsModalOpen from '@ui/modal/use-is-modal-open';
import { updateMeetingInviteSlice } from '@/store/slices/project';
import { updateSpeaker } from '@/store/slices/speakers';
import EditCompany from './components/edit-company';
import usePulseElement from '@/utils/use-pulse-element';

type TInviteCastModalProps = {
  open: boolean;
  initialName?: string;
  defaultSpeaker?: Speaker | null;
  onClose: () => void;
  handleNewCast?: (speaker: Speaker) => void;
  handleEditCast?: (speaker: Speaker) => void;
  onTransitionClosed?: () => void;
};

const InviteCastModal = memo(
  ({
    defaultSpeaker,
    open,
    initialName,
    onClose,
    handleNewCast,
    handleEditCast,
    onTransitionClosed,
  }: TInviteCastModalProps) => {
    const imageContainerRef = useRef<HTMLDivElement | null>(null);
    const pulseImageContainer = usePulseElement(imageContainerRef, { borderRadius: '16px' });

    const languages = useAppSelector(selectWorkingProjectLanguages);
    const userChannel = useAppSelector(selectUserChannel);
    const channelAdmins = useAppSelector(selectAdminContacts);
    const meetings = useAppSelector(selectGreenroomSessionMeetings);
    const projectName = useAppSelector(selectProjectName);

    const sessionUuid = useParamsOrFirstSessionUuid();

    useLoadWorkingSessionMeetingData({ sessionUuid });

    const sessionMeetingData = meetings?.[sessionUuid || ''] || null;

    const dispatch = useAppDispatch();

    const [workingSpeaker, setWorkingSpeaker] = useState<CreateSpeaker | Speaker>(
      defaultSpeaker || createDefaultSpeaker()
    );

    const [openInviteModal, setOpenInviteModal] = useState(false);
    const [savingCast, setSavingCast] = useState(false);
    const [invitingCast, setInvitingCast] = useState(false);
    const [greenroomProfile, setGreenroomProfile] = useState<GreenroomUserProfile | undefined>(undefined);

    const _baseLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
    const baseLanguage = _baseLanguage || 'en';

    const paramsLanguage = useLanguageParams();
    const language = paramsLanguage || baseLanguage;

    const onModalTransitionClosed = useCallback(() => {
      setWorkingSpeaker(defaultSpeaker || createDefaultSpeaker());
      onTransitionClosed?.();
    }, [defaultSpeaker, onTransitionClosed]);

    const nameGuesses = useInitializeNameGuess({
      initialName,
      defaultSpeaker,
    });

    const onTransitionOpening = useCallback(() => {
      setWorkingSpeaker({
        ...(defaultSpeaker || createDefaultSpeaker()),
        email: nameGuesses.email,
        first_name: nameGuesses.first_name,
        last_name: nameGuesses.last_name,
      });
    }, [defaultSpeaker, nameGuesses.email, nameGuesses.first_name, nameGuesses.last_name]);

    // Note: do not use a single handleChange for the speaker fields,
    // it will cause unnecessary re-renders of every field on every change.
    // Each field should have its own handler that only updates that specific field in the workingSpeaker state.
    const handleFirstNameChange = useCallback((value: string) => {
      setWorkingSpeaker((prev) => ({ ...prev, first_name: value }));
    }, []);

    const handleLastNameChange = useCallback((value: string) => {
      setWorkingSpeaker((prev) => ({ ...prev, last_name: value }));
    }, []);

    const handleJobTitleChange = useCallback((value: TranslateString) => {
      setWorkingSpeaker((prev) => ({ ...prev, job_title: value }));
    }, []);

    const handleLocationChange = useCallback((value: string) => {
      setWorkingSpeaker((prev) => ({ ...prev, location: value }));
    }, []);

    const handleEmailChange = useCallback((value: string) => {
      setWorkingSpeaker((prev) => ({ ...prev, email: value }));
    }, []);

    const handleDescriptionChange = useCallback((value: TranslateString) => {
      setWorkingSpeaker((prev) => ({ ...prev, description: value }));
    }, []);

    const handleCompanyChange = useCallback((value: TranslateString) => {
      setWorkingSpeaker((prev) => ({ ...prev, company: value }));
    }, []);

    const { validEmail, validSpeaker, imageLikelyForgotten } = useValidateWorkingSpeaker({
      workingSpeaker,
      greenroomProfile: greenroomProfile || null,
    });

    const cropModalOpen = useIsModalOpen('upload-cast-image-cropper');

    const handleCloseCastModal = useCallback(() => {
      if (openInviteModal || cropModalOpen) return;

      setGreenroomProfile(undefined);

      onClose();
    }, [cropModalOpen, onClose, openInviteModal]);

    const hasAnyChanges = useMemo(() => {
      if (!defaultSpeaker) {
        return (
          workingSpeaker.first_name?.trim() ||
          workingSpeaker.last_name?.trim() ||
          workingSpeaker.job_title?.base?.trim() ||
          workingSpeaker.location?.trim() ||
          workingSpeaker.email?.trim() ||
          workingSpeaker.description?.base?.trim() ||
          workingSpeaker.company?.base?.trim() ||
          workingSpeaker.image
        );
      }

      return (
        workingSpeaker.first_name !== (defaultSpeaker.first_name || '') ||
        workingSpeaker.last_name !== (defaultSpeaker.last_name || '') ||
        (workingSpeaker.job_title?.base || '') !== (defaultSpeaker.job_title?.base || '') ||
        (workingSpeaker.location || '') !== (defaultSpeaker.location || '') ||
        (workingSpeaker.email || '') !== (defaultSpeaker.email || '') ||
        (workingSpeaker.description?.base || '') !== (defaultSpeaker.description?.base || '') ||
        (workingSpeaker.company?.base || '') !== (defaultSpeaker.company?.base || '') ||
        (workingSpeaker.image || '') !== (defaultSpeaker.image || '')
      );
    }, [defaultSpeaker, workingSpeaker]);

    return (
      <>
        <Modal
          id="invite-cast-member-modal"
          open={open}
          onRequestClose={hasAnyChanges ? undefined : handleCloseCastModal}
          onTransitionClosed={onModalTransitionClosed}
          onTransitionOpening={onTransitionOpening}
          customSize={{
            minHeight: '200px',
            maxHeight: '532px',
            height: '532px',
            width: '640px',
            maxWidth: 'calc(100vw - 40px)',
            minWidth: '256px',
          }}
          RenderFooterContent={() => (
            <Container
              borderRadius="0 0 24px 24px"
              design="GlobalCont1"
              className="flex ai-center jc-flex-end"
              width="100%"
              height="80px"
              padding="16px 32px 24px 16px"
            >
              <div className="flex ai-center gap-8">
                <Button design="LocalBttnSec" designSize="medium" onClick={handleCloseCastModal}>
                  <Text className="L1">Cancel</Text>
                </Button>
                <Button
                  design="LocalBttnPrim"
                  designSize="medium"
                  disabled={(!validSpeaker && !imageLikelyForgotten) || savingCast || invitingCast}
                  onClick={() => {
                    if (imageLikelyForgotten) {
                      pulseImageContainer();
                      return;
                    }
                    handleSaveSpeaker({
                      userChannel,
                      setSavingCast,
                      defaultSpeaker,
                      workingSpeaker,
                      baseLanguage,
                      languages,
                      greenroomProfile,
                      meeting: sessionMeetingData?.meeting,
                      setGreenroomProfile,
                      dispatch,
                      handleNewCast,
                      handleEditCast,
                      channelAdmins,
                    });
                  }}
                >
                  <Text className="L1">Save</Text>
                </Button>
              </div>
            </Container>
          )}
          footerClassName="width-100-percent"
          contentClassName="min-width-fit-content radius-t-l-24 radius-t-r-24"
          contentWrapperClassName="max-width-100-percent overflow-auto"
          backgroundColor="inherit"
        >
          <Container
            borderRadius="24px 24px 0 0"
            minHeight="100%"
            className="flex fw-wrap gap-24 flex-column-mobile"
            padding="32px 32px 0 32px"
            design="GlobalCont1"
            width="100%"
          >
            <Container
              height="fit-content"
              maxWidth="200px"
              width="100%"
              className="flex-1 flex jc-center"
              ref={imageContainerRef}
            >
              <Container height="200px" width="200px" className="flex ai-center jc-center">
                <UploadCastImage workingSpeaker={workingSpeaker} setWorkingSpeaker={setWorkingSpeaker} />
              </Container>
            </Container>

            <Container width="100%" maxWidth="calc(640px - 200px)" className="flex-1 flex-column gap-8">
              <Container
                background="var(--local-chip-input-default-bg)"
                border="2px solid var(--local-chip-input-default-outline)"
                borderRadius="100px"
                padding="8px 16px"
                height="32px"
                width="fit-content"
                className="flex pi-center"
              >
                <Text className="S3" design="GlobalIcoText1">
                  Speaker
                </Text>
              </Container>

              <div className="flex ai-center gap-16 jc-space-evenly flex-column-small">
                <EditFirstName firstName={workingSpeaker.first_name} onChange={handleFirstNameChange} />

                <EditLastName lastName={workingSpeaker.last_name} onChange={handleLastNameChange} />
              </div>

              <div className="flex ai-center gap-16 jc-space-evenly flex-column-small">
                <EditCompany
                  company={workingSpeaker.company}
                  onChange={handleCompanyChange}
                  baseLanguage={baseLanguage}
                  language={language}
                />

                <EditLocation location={workingSpeaker.location || ''} onChange={handleLocationChange} />
              </div>

              <div>
                <EditTitle
                  jobTitle={workingSpeaker.job_title}
                  onChange={handleJobTitleChange}
                  baseLanguage={baseLanguage}
                  language={language}
                />
              </div>

              <EditEmail
                email={workingSpeaker.email || ''}
                onChange={handleEmailChange}
                greenroomProfile={greenroomProfile}
                validEmail={validEmail}
                setOpenInviteModal={setOpenInviteModal}
                savingCast={savingCast}
                invitingCast={invitingCast}
              />

              <EditBio
                description={workingSpeaker.description}
                onChange={handleDescriptionChange}
                baseLanguage={baseLanguage}
                language={language}
              />
            </Container>
          </Container>
        </Modal>

        <SendInviteModal
          open={openInviteModal}
          email={workingSpeaker.email || ''}
          onClose={() => setOpenInviteModal(false)}
          handleSendGreenroomInvite={async () => {
            const result = await handleSendGreenroomInvite({
              workingSpeaker,
              setWorkingSpeaker,
              userChannel,
              meeting: sessionMeetingData?.meeting,
              setInvitingCast,
              setGreenroomProfile,
              dispatch,
              setOpenInviteModal,
              sessionUuid,
              projectName,
            });
            if (result.inviteResponse?.code && sessionUuid) {
              dispatch(
                updateMeetingInviteSlice({
                  code: result.inviteResponse.code,
                  sessionUuid,
                  ...result.inviteResponse,
                })
              );

              // always update invite status if sending invite if we have a defaultSpeaker.
              if (defaultSpeaker) {
                const email = result.inviteResponse?.profile?.email || defaultSpeaker.email;
                const matchedNewAdmin = result.admins?.find((admin) => admin.email === email);
                dispatch(
                  updateSpeaker({
                    originalSpeaker: defaultSpeaker,
                    baseLanguage,
                    languages: languages || [baseLanguage],
                    speaker: {
                      ...defaultSpeaker,
                      invite_status:
                        matchedNewAdmin?.verified || defaultSpeaker?.bl_admin?.verified
                          ? ESpeakerInviteStatus.confirmed
                          : ESpeakerInviteStatus.pending,
                    },
                  })
                );
              }
            }
          }}
          sendingInvite={invitingCast}
        />
      </>
    );
  }
);

InviteCastModal.displayName = 'InviteCastModal';

export default InviteCastModal;
